import React, { createContext, useContext, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { authService } from "../services/authService";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => authService.getStoredUser()); // ✅ preload from localStorage
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const user = await authService.getMe();
          setUser(user);
          authService.storeUser(user); // ✅ sync again to be sure
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        authService.logout();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    try {
      setError(null);
      const response = await authService.login(credentials);
      const userPayload = response?.data?.user ?? response?.user;
      if (!userPayload) {
        throw new Error("Invalid login response: missing user");
      }
      authService.storeToken(response.token);
      authService.storeUser(userPayload);
      setUser(userPayload);
      toast.success("Signed in successfully");
      return response;
    } catch (error) {
      const msg = error.response?.data?.message || "Login failed";
      setError(msg);
      toast.error(msg);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await authService.register(userData);
      const userPayload = response?.data?.user ?? response?.user;
      if (!userPayload) {
        throw new Error("Invalid registration response: missing user");
      }
      authService.storeToken(response.token);
      authService.storeUser(userPayload);
      setUser(userPayload);
      toast.success("Account created");
      return response;
    } catch (error) {
      const msg = error.response?.data?.message || "Registration failed";
      setError(msg);
      toast.error(msg);
      throw error;
    }
  };

  const registerAsAgent = async (agentData) => {
    try {
      setError(null);
      const response = await authService.registerAsAgent(agentData);
      const updatedUser = await authService.getMe();
      authService.storeUser(updatedUser); // ✅ fix
      setUser(updatedUser);
      return response;
    } catch (error) {
      const msg = error.response?.data?.message || "Agent registration failed";
      setError(msg);
      toast.error(msg);
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setError(null);
  };

  const updateUser = (updatedUserData) => {
    const updatedUser = { ...user, ...updatedUserData };
    setUser(updatedUser);
    authService.storeUser(updatedUser);
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    registerAsAgent,
    logout,
    updateUser,
    clearError,
    isAuthenticated: !!user,
    isAgent: user?.role === "agent",
    isAdmin: user?.role === "admin",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
